import {
  getSearchKey,
  getWorkspaceId,
  InvoiceRequest,
  useAppSelector,
} from '@zonofi/zono-money-store';
import moment from 'moment';
import { useDebounceSearch } from './use-debounce-search';

interface ApiConfig<T = any> {
  api: (params: T) => any;
  searchKeyMinLen: number;
  buildParams: (...args: any[]) => T;
}

export const useSearchListApi = ({
  fetchOrderSearchApi = (params: any) => {},
  fetchCustomerSearchApi = (params: any) => {},
  fetchTeamMemberSearchApi = (params: any) => {},
  fetchProductSearchApi = (params: any) => {},
  fetchInvoiceSearchApi = (params: any) => {},
}: {
  fetchOrderSearchApi?: (params: any) => any;
  fetchCustomerSearchApi?: (params: any) => any;
  fetchTeamMemberSearchApi?: (params: any) => any;
  fetchProductSearchApi?: (params: any) => any;
  fetchInvoiceSearchApi?: (params: any) => any;
}) => {
  const sellerWorkspaceId = useAppSelector(getWorkspaceId);
  const { debouncedSearchValue } = useDebounceSearch();

  const searchListParamIdToApiMap: Record<string, ApiConfig> = {
    customerId: {
      api: fetchCustomerSearchApi,
      searchKeyMinLen: 2,
      buildParams: () => ({
        workspaceId: sellerWorkspaceId,
        searchKey: debouncedSearchValue,
      }),
    },
    searchKeyword: {
      api: fetchOrderSearchApi,
      searchKeyMinLen: 3,
      buildParams: (appliedDateRange) => ({
        params: { workspaceId: sellerWorkspaceId },
        body: {
          workspaceId: sellerWorkspaceId,
          customerId: '',
          skip: 1,
          sortBy: 'orderPlacedAt',
          sortDirection: 'DESC',
          includeInvoice: true,
          startDate: moment(appliedDateRange?.startDate)
            .startOf('day')
            .format(),
          endDate: moment(appliedDateRange?.endDate)
            .add(1, 'days')
            .startOf('day')
            .format(),
          filterModel: {
            status: [],
          },
          pageNo: 1,
          pageSize: 20,
          searchKeyword: debouncedSearchValue,
        },
      }),
    },
    userId: {
      api: fetchTeamMemberSearchApi,
      searchKeyMinLen: 2,
      buildParams: () => ({
        workspaceId: sellerWorkspaceId,
        sellerWorkspaceId,
        includeInactive: true,
        includePendingInvites: true,
        includeTeamCustomersMapping: true,
        searchKey: debouncedSearchValue,
      }),
    },
    invoiceId: {
      api: fetchInvoiceSearchApi,
      searchKeyMinLen: 2,
      buildParams: () =>
        ({
          workspaceId: sellerWorkspaceId,
          searchKey: debouncedSearchValue,
        }) as InvoiceRequest,
    },
    productVariantIds: {
      api: fetchProductSearchApi,
      searchKeyMinLen: 2,
      buildParams: () => ({
        workspaceId: sellerWorkspaceId,
        params: {
          customerId: '',
          pageSize: 20,
        },
        body: {
          searchKey: debouncedSearchValue,
        },
      }),
    },
  };

  const fetchSearchListApi = ({
    paramId,
    value,
    appliedDateRange = {},
  }: {
    paramId: string;
    value: string;
    appliedDateRange?: any;
  }) => {
    const config = searchListParamIdToApiMap[paramId];

    if (!config) {
      throw new Error(`No API configuration found for paramId: ${paramId}`);
    }

    const params = config.buildParams(
      value,
      sellerWorkspaceId,
      appliedDateRange
    );

    return (
      config.searchKeyMinLen <= value.length && config.api(params).unwrap()
    );
  };

  return {
    searchListParamIdToApiMap,
    fetchSearchListApi,
  };
};
